import { cloneDeep } from 'lodash';

import {
    ADD_METRIKA_KEY_GOAL,
    CLOSE_METRIKA_KEY_GOALS_EDITOR,
    DELETE_METRIKA_KEY_GOAL,
    INIT_METRIKA_KEY_GOALS_EDITORS,
    InitKeyGoalsEditorsItemData,
    MetrikaKeyGoalsEditorAction,
    OPEN_METRIKA_KEY_GOALS_EDITOR,
    RESET_METRIKA_KEY_GOALS_EDITOR,
    SELECT_METRIKA_KEY_GOAL,
    SHOW_METRIKA_KEY_GOALS_EDITOR_ERRORS,
    SHOW_METRIKA_KEY_GOALS_EDITOR_METRIKA_IS_UNAVAILABLE,
    VALIDATE_METRIKA_KEY_GOAL_CONVERSION_VALUE
} from '../actions/metrikaKeyGoals';
import { mapStringToCurrencySymbol } from '../mappers/common/common';
import { selectMetrikaKeyGoalsConstants } from '../selectors/metrikaKeyGoalsEditor/metrikaKeyGoalsEditor';
import {
    getIsInvolvedSessionsOnly,
    validateMetrikaKeyGoalConversion
} from '../service/MetrikaKeyGoalsEditorHelpers/MetrikaKeyGoalsEditorHelpers';
import {
    MetrikaKeyGoalsEditorData,
    MetrikaKeyGoalsEditorState,
    MetrikaKeyGoalSpecialId
} from '../typings/metrikaKeyGoals';
import { State } from '../typings/state';

export const metrikaKeyGoalsEditorInitialState: MetrikaKeyGoalsEditorData = {
    isOpen: false,
    selectedGoals: [],
    isMetrikaAvailable: true
};

export function metrikaKeyGoalsEditor(
    state: MetrikaKeyGoalsEditorState = {},
    action: MetrikaKeyGoalsEditorAction,
    fullState: State
): MetrikaKeyGoalsEditorState {
    switch (action.type) {
        case INIT_METRIKA_KEY_GOALS_EDITORS: {
            const editorsData = action.editors.reduce(
                (
                    res: MetrikaKeyGoalsEditorState,
                    { editorId, savedValue }: InitKeyGoalsEditorsItemData
                ) => {
                    res[editorId] = {
                        ...metrikaKeyGoalsEditorInitialState,
                        selectedGoals: savedValue
                    };

                    return res;
                },
                {}
            );

            return {
                ...state,
                ...editorsData
            };
        }

        case OPEN_METRIKA_KEY_GOALS_EDITOR: {
            const currentState =
                state[action.editorId] || metrikaKeyGoalsEditorInitialState;

            return {
                ...state,
                [action.editorId]: {
                    ...currentState,
                    isOpen: true
                }
            };
        }

        case CLOSE_METRIKA_KEY_GOALS_EDITOR: {
            const currentState =
                state[action.editorId] || metrikaKeyGoalsEditorInitialState;

            return {
                ...state,
                [action.editorId]: {
                    ...currentState,
                    isOpen: false
                }
            };
        }

        case RESET_METRIKA_KEY_GOALS_EDITOR: {
            const currentState =
                state[action.editorId] || metrikaKeyGoalsEditorInitialState;

            return {
                ...state,
                [action.editorId]: {
                    ...currentState,
                    selectedGoals: action.savedValue
                }
            };
        }

        case SELECT_METRIKA_KEY_GOAL: {
            const currentState =
                state[action.editorId] || metrikaKeyGoalsEditorInitialState;

            const newSelectedGoals = cloneDeep(
                state[action.editorId].selectedGoals
            );

            newSelectedGoals[action.selectedGoalIndex].id = action.goalId;
            newSelectedGoals[action.selectedGoalIndex].errors.goal = [];

            const isInvolvedSessionsOnly =
                currentState.selectedGoals.length === 1 &&
                action.goalId === action.involvedSessionsId;
            const { conversionValue } = newSelectedGoals[
                action.selectedGoalIndex
            ];

            if (isInvolvedSessionsOnly && conversionValue === '') {
                newSelectedGoals[
                    action.selectedGoalIndex
                ].errors.conversionValue = [];
            }

            return {
                ...state,
                [action.editorId]: {
                    ...currentState,
                    selectedGoals: [...newSelectedGoals]
                }
            };
        }

        case ADD_METRIKA_KEY_GOAL: {
            const currentState =
                state[action.editorId] || metrikaKeyGoalsEditorInitialState;

            const newSelectedGoals = cloneDeep(
                state[action.editorId].selectedGoals
            );
            newSelectedGoals.push({
                id: MetrikaKeyGoalSpecialId.chooseGoal,
                conversionValue: String(action.minConversion),
                errors: {
                    goal: [],
                    conversionValue: []
                }
            });

            return {
                ...state,
                [action.editorId]: {
                    ...currentState,
                    selectedGoals: [...newSelectedGoals]
                }
            };
        }

        case DELETE_METRIKA_KEY_GOAL: {
            const currentState =
                state[action.editorId] || metrikaKeyGoalsEditorInitialState;

            const newSelectedGoals = cloneDeep(
                state[action.editorId].selectedGoals
            );
            newSelectedGoals.splice(action.goalIndex, 1);

            const isInvolvedSessionsOnly = getIsInvolvedSessionsOnly(
                newSelectedGoals,
                action.involvedSessionsId
            );

            if (isInvolvedSessionsOnly) {
                const [involvedSessionsGoal] = newSelectedGoals;
                const { conversionValue } = involvedSessionsGoal;

                if (conversionValue === '') {
                    involvedSessionsGoal.errors.conversionValue = [];
                }
            }

            return {
                ...state,
                [action.editorId]: {
                    ...currentState,
                    selectedGoals: [...newSelectedGoals]
                }
            };
        }

        case VALIDATE_METRIKA_KEY_GOAL_CONVERSION_VALUE: {
            const metrikaKeyGoalsConstants = selectMetrikaKeyGoalsConstants(
                fullState
            );
            const { workCurrency } = fullState.commonData.client;

            const currentState =
                state[action.editorId] || metrikaKeyGoalsEditorInitialState;

            const isInvolvedSessionsOnly = getIsInvolvedSessionsOnly(
                currentState.selectedGoals,
                action.involvedSessionsId
            );

            const validationErrors = validateMetrikaKeyGoalConversion(
                metrikaKeyGoalsConstants,
                mapStringToCurrencySymbol(workCurrency),
                isInvolvedSessionsOnly,
                action.conversionValue
            );

            const newSelectedGoals = cloneDeep(
                state[action.editorId].selectedGoals
            );
            newSelectedGoals[action.selectedGoalIndex].conversionValue =
                action.conversionValue;
            newSelectedGoals[
                action.selectedGoalIndex
            ].errors.conversionValue = validationErrors;

            return {
                ...state,
                [action.editorId]: {
                    ...currentState,
                    selectedGoals: [...newSelectedGoals]
                }
            };
        }

        case SHOW_METRIKA_KEY_GOALS_EDITOR_ERRORS: {
            const currentState =
                state[action.editorId] || metrikaKeyGoalsEditorInitialState;

            const newSelectedGoals = cloneDeep(
                state[action.editorId].selectedGoals
            );
            newSelectedGoals.forEach((goal, index) => {
                goal.errors = action.validationErrors[index];
            });

            return {
                ...state,
                [action.editorId]: {
                    ...currentState,
                    selectedGoals: [...newSelectedGoals]
                }
            };
        }

        case SHOW_METRIKA_KEY_GOALS_EDITOR_METRIKA_IS_UNAVAILABLE: {
            const currentState =
                state[action.editorId] || metrikaKeyGoalsEditorInitialState;

            return {
                ...state,
                [action.editorId]: {
                    ...currentState,
                    isMetrikaAvailable: false
                }
            };
        }

        default:
            return state;
    }
}
